import { useEffect, useMemo, useState } from 'react';
import { equipesApi } from '../../api/resources';
import { _inp } from '../../components/ui/Inputs.jsx';
import { tr, trf } from '../../lib/i18n';
import { equipeDe } from '../../lib/equipes';
import { nomDuChantier } from '../../lib/nom-chantier';
import { collaborateursEnPoste } from '../../lib/personnel';
import { C } from '../../theme/palette';

/**
 * « Qui va sur quel chantier » : le patron ou le directeur des travaux choisit
 * le chantier, le technicien qui le dirige, puis les employés. Deux façons :
 * faire l'équipe lui-même, tout de suite, ou l'envoyer au technicien, qui la
 * crée d'un toucher (et peut encore retirer quelqu'un).
 */

const idServeur = (id) => typeof id === 'number' && id > 0;
const styleTitre = { fontSize: 12, fontWeight: 800, color: C.grey, letterSpacing: 0.5 };
const styleChamp = { ..._inp, fontSize: 14, minHeight: 44, boxSizing: 'border-box' };

function Puce({ on, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      style={{
        padding: '9px 12px',
        minHeight: 40,
        borderRadius: 99,
        border: on ? 'none' : '1px solid ' + C.border,
        background: on ? C.teal : C.white,
        color: on ? C.white : C.ink,
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      {on ? '✓ ' : ''}
      {children}
    </button>
  );
}

/**
 * @param {object} props
 * @param {object[]} props.employes les employés
 * @param {object[]} props.chantiers les chantiers
 * @param {object[]} props.equipes les équipes (pour dire où est déjà chacun)
 * @param {() => void} props.onFini relit les équipes
 * @param {() => void} props.onAnnule ferme
 */
export function QuiVaSurQuelChantier({
  employes = [],
  chantiers = [],
  equipes = [],
  onFini,
  onAnnule,
}) {
  const [chefs, setChefs] = useState([]);
  const [chantierId, setChantierId] = useState('');
  const [chefId, setChefId] = useState(null);
  const [employeIds, setEmployeIds] = useState([]);
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [fait, setFait] = useState('');

  useEffect(() => {
    Promise.resolve()
      .then(() => equipesApi.chefsPossibles())
      .then((l) => setChefs(Array.isArray(l) ? l : []))
      .catch(() => {});
  }, []);

  const personnes = useMemo(
    () => collaborateursEnPoste(employes).filter((e) => idServeur(e.id)),
    [employes],
  );
  const enCours = useMemo(
    () =>
      (chantiers || []).filter(
        (c) => c && idServeur(c.id) && c.statut !== 'Terminé' && c.statut !== 'Annulé',
      ),
    [chantiers],
  );
  const chef = chefs.find((c) => c.id === chefId);
  const prenomChef = chef ? String(chef.nom || '').split(' ')[0] : '';
  const pret = chantierId && chefId && employeIds.length > 0;

  const agit = async (proposer) => {
    if (!pret) {
      setErreur(tr('Choisis le chantier, le technicien et au moins un employé.'));
      return;
    }
    setEnvoi(true);
    setErreur('');
    const corps = {
      chefId,
      chantierId: Number(chantierId),
      employeIds,
      message: message.trim() || null,
    };
    try {
      if (proposer) await equipesApi.propose(corps);
      else await equipesApi.affecte(corps);
      setFait(
        proposer
          ? trf('Envoyé à {0}, qui crée son équipe d’un toucher.', prenomChef)
          : trf('C’est fait : l’équipe de {0} est prête, tout le monde est prévenu.', prenomChef),
      );
      onFini?.();
    } catch (e) {
      setErreur(e?.message || tr("L'équipe n'a pas pu être enregistrée."));
    } finally {
      setEnvoi(false);
    }
  };

  if (fait) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div
          role="status"
          style={{
            fontSize: 13.5,
            color: C.green,
            background: C.greenL,
            padding: 12,
            borderRadius: 12,
          }}
        >
          ✓ {fait}
        </div>
        <button
          type="button"
          onClick={onAnnule}
          style={{
            border: '1px solid ' + C.border,
            background: C.white,
            borderRadius: 12,
            padding: 11,
            fontWeight: 700,
          }}
        >
          {tr('Fermer')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-qui-va>
      <div style={styleTitre}>{tr('1 · LE CHANTIER')}</div>
      <select
        aria-label={tr('Le chantier')}
        value={chantierId}
        onChange={(e) => setChantierId(e.target.value)}
        style={styleChamp}
      >
        <option value="">{tr('— Choisir le chantier —')}</option>
        {enCours.map((c) => (
          <option key={c.id} value={c.id}>
            {nomDuChantier(c, enCours)}
          </option>
        ))}
      </select>
      <div style={styleTitre}>{tr('2 · LE TECHNICIEN QUI LE DIRIGE')}</div>
      {chefs.length === 0 ? (
        <div style={{ fontSize: 12.5, color: C.grey }}>
          {tr("Aucun technicien ni secrétaire n'a encore de compte.")}
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {chefs.map((c) => (
            <Puce key={c.id} on={chefId === c.id} onClick={() => setChefId(c.id)}>
              {String(c.nom || '').split(' ')[0]}
            </Puce>
          ))}
        </div>
      )}
      <div style={styleTitre}>{tr('3 · SES EMPLOYÉS')}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {personnes.map((e) => {
          const ailleurs = equipeDe(equipes, 'employe', e.id);
          const sienne = ailleurs && ailleurs.chefId === chefId;
          return (
            <Puce
              key={e.id}
              on={employeIds.includes(e.id)}
              onClick={() =>
                setEmployeIds((l) =>
                  l.includes(e.id) ? l.filter((x) => x !== e.id) : [...l, e.id],
                )
              }
            >
              {e.prenom || String(e.nom || '').split(' ')[0]}
              {ailleurs && !sienne ? ' · ' + ailleurs.nom : ''}
            </Puce>
          );
        })}
      </div>
      <label
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          fontSize: 12,
          fontWeight: 700,
          color: C.grey,
        }}
      >
        {tr('Message (facultatif)')}
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={tr('Ex. : Lundi 7h, on commence la façade.')}
          maxLength={2000}
          style={{ ...styleChamp, minHeight: 60 }}
        />
      </label>
      {erreur && (
        <div role="alert" style={{ fontSize: 12.5, color: C.red }}>
          {erreur}
        </div>
      )}
      <button
        type="button"
        disabled={envoi}
        onClick={() => agit(false)}
        style={{
          border: 'none',
          background: C.green,
          color: C.white,
          borderRadius: 14,
          padding: 14,
          fontSize: 14.5,
          fontWeight: 800,
          cursor: 'pointer',
        }}
      >
        ✓ {tr("Faire l'équipe moi-même")}
      </button>
      <button
        type="button"
        disabled={envoi}
        onClick={() => agit(true)}
        style={{
          border: '2px solid ' + C.teal,
          background: C.white,
          color: C.teal,
          borderRadius: 14,
          padding: 13,
          fontSize: 14.5,
          fontWeight: 800,
          cursor: 'pointer',
        }}
      >
        📨{' '}
        {prenomChef
          ? trf('Envoyer à {0}, qui fait son équipe', prenomChef)
          : tr('Envoyer au technicien, qui fait son équipe')}
      </button>
      <button
        type="button"
        onClick={onAnnule}
        style={{
          border: '1px solid ' + C.border,
          background: C.white,
          borderRadius: 12,
          padding: 10,
          fontWeight: 700,
        }}
      >
        {tr('Annuler')}
      </button>
    </div>
  );
}
